/**
 * RepBuddy - Structured chat blocks (SCRUM-41)
 *
 * Renders the optional `blocks` array returned by /api/chat/respond so a chat
 * reply reads as what it is: a list renders as a list, a ranking or comparison
 * renders as a table, and a drafted email renders as a distinct copyable card.
 *
 * Blocks are already validated and capped by the backend
 * (services/chatFormat.js); this component still guards against missing fields
 * so a bad payload renders nothing rather than crashing the chat.
 */

import { useState, useRef, useEffect } from 'react';
import { renderInline, renderProse } from './inlineMarkdown';
import CopyButton from '../common/CopyButton';
import { retoneEmail } from '../../services/hunter';
import styles from './ChatBlocks.module.css';

// Voices offered by the tone selector - must match backend TONE_PRESETS keys.
const TONES = ['Conversational', 'Direct & Blunt', 'Professional', 'Witty / Humorous'];

// A multi-part answer: one headed section per thing the user asked. Headings are
// small caps labels so the eye finds each part; bodies are real paragraphs.
function SectionsBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (items.length === 0) return null;

  return (
    <div className={styles.block}>
      {block.title && <div className={styles.blockTitle}>{block.title}</div>}
      <div className={styles.sections}>
        {items.map((s, i) => (
          <section key={i} className={styles.section}>
            {s.heading && <h4 className={styles.sectionHeading}>{renderInline(s.heading, `sh${i}`)}</h4>}
            <div className={styles.sectionBody}>{renderProse(s.body, `sb${i}`)}</div>
          </section>
        ))}
      </div>
    </div>
  );
}

function ListBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (items.length === 0) return null;

  const ListTag = block.ordered ? 'ol' : 'ul';
  return (
    <div className={styles.block}>
      {block.title && <div className={styles.blockTitle}>{block.title}</div>}
      <ListTag className={block.ordered ? styles.orderedList : styles.bulletList}>
        {items.map((item, i) => <li key={i}>{renderInline(item, `li${i}`)}</li>)}
      </ListTag>
    </div>
  );
}

function TableBlock({ block }) {
  const columns = Array.isArray(block.columns) ? block.columns : [];
  const rows    = Array.isArray(block.rows)    ? block.rows    : [];
  if (columns.length === 0 || rows.length === 0) return null;

  return (
    <div className={styles.block}>
      {block.title && <div className={styles.blockTitle}>{block.title}</div>}
      {/* Wrapper scrolls on its own so a wide table never widens the chat column. */}
      <div className={styles.tableScroll}>
        {/* Labelled via aria-label rather than a visually-hidden <caption>:
            hiding a caption needs absolute positioning, which this app cannot
            afford in the chat flow (see CopyButton). */}
        <table className={styles.table} aria-label={block.title || undefined}>
          <thead>
            <tr>{columns.map((col, i) => <th key={i} scope="col">{col}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {columns.map((_, c) => (
                  <td key={c}>{renderInline((row || [])[c] || '—', `c${r}-${c}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The drafted email is editable in place, mirroring the LinkedIn research draft:
// type into the box, tweak the subject, then copy. Edits are held in local state
// for snappy typing and committed on blur via onEditBlock, which writes them back
// into the chat so they persist across reload (see VerifierPage render site).
function EmailBlock({ block, index, onEditBlock, sellerProfile }) {
  const [subject, setSubject] = useState(block.subject || '');
  const [body,    setBody]    = useState(block.body || '');
  const [activeTone,  setActiveTone]  = useState(block.activeTone || 'Original');
  const [loadingTone, setLoadingTone] = useState(null);
  const [toneError,   setToneError]   = useState(null);
  const hasSubject = block.subject !== undefined && block.subject !== null;
  const bodyRef = useRef(null);

  // Grow the textarea to fit its content so the whole draft is visible.
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.style.height = 'auto';
    bodyRef.current.style.height = bodyRef.current.scrollHeight + 'px';
  }, [body]);

  if (!block.body) return null;

  // Subject is only ever persisted for emails that actually have one, so
  // restoring/retoning a subject-less email never spawns a phantom subject field.
  const persist = (next, extra = {}) => {
    if (!onEditBlock) return;
    const patch = { body: next.body, ...extra };
    if (hasSubject) patch.subject = next.subject;
    onEditBlock(index, patch);
  };

  const applyEmail = (next) => {
    setBody(next.body || '');
    setSubject(next.subject || '');
  };

  // Commit only when something actually changed, so an idle focus/blur is a no-op.
  // An edit to a tone variant is folded back into that variant's cache slot too.
  const commit = () => {
    if (!onEditBlock) return;
    const patch = {};
    if (body !== (block.body || '')) patch.body = body;
    if (hasSubject && subject !== (block.subject || '')) patch.subject = subject;
    if (Object.keys(patch).length === 0) return;
    if (activeTone && activeTone !== 'Original') {
      patch.toneVariants = { ...(block.toneVariants || {}), [activeTone]: { subject, body } };
    } else if (block.baseEmail) {
      // Editing the original after retoning: keep the stored base in sync so
      // toggling back to Original doesn't resurrect the pre-edit text.
      patch.baseEmail = { subject, body };
    }
    onEditBlock(index, patch);
  };

  const selectTone = async (tone) => {
    if (tone === activeTone || loadingTone) return;
    setToneError(null);

    // Original = the draft as it was before any retoning (captured lazily below).
    if (tone === 'Original') {
      const base = block.baseEmail || { subject: block.subject || '', body: block.body || '' };
      applyEmail(base);
      setActiveTone('Original');
      persist(base, { activeTone: 'Original' });
      return;
    }

    // Already generated: swap instantly from cache.
    const cached = block.toneVariants?.[tone];
    if (cached) {
      applyEmail(cached);
      setActiveTone(tone);
      persist(cached, { activeTone: tone });
      return;
    }

    // First time for this tone: rewrite from the base (current text, so any
    // manual edits made before the first retone become the stable baseline).
    const base = block.baseEmail || { subject, body };
    setLoadingTone(tone);
    try {
      const result = await retoneEmail({ subject: base.subject, body: base.body, tone, sellerProfile });
      const next = { subject: result.subject || '', body: result.body || '' };
      applyEmail(next);
      setActiveTone(tone);
      persist(next, {
        activeTone: tone,
        baseEmail: base,
        toneVariants: { ...(block.toneVariants || {}), [tone]: next },
      });
    } catch (err) {
      setToneError(err.message || 'Could not rewrite in that tone.');
    } finally {
      setLoadingTone(null);
    }
  };

  // Copy what the user would actually paste, using the live edited text.
  const copyText = hasSubject ? `Subject: ${subject}\n\n${body}` : body;

  return (
    <div className={styles.emailCard}>
      <div className={styles.emailHeader}>
        <span className={styles.emailIcon} aria-hidden="true">✉</span>
        <span className={styles.emailLabel}>Draft email</span>
        <CopyButton
          text={copyText}
          className={styles.copyBtn}
          doneClass={styles.copyBtnDone}
          failedClass={styles.copyBtnFailed}
          title="Copy this draft"
          ariaLabel="Copy email draft"
        />
      </div>

      <div className={styles.toneToolbar} role="group" aria-label="Rewrite tone">
        <span className={styles.toneLabel}>Tone</span>
        {['Original', ...TONES].map(t => {
          const isActive  = activeTone === t;
          const isLoading = loadingTone === t;
          return (
            <button
              key={t}
              type="button"
              className={`${styles.toneBtn} ${isActive ? styles.toneBtnOn : ''}`}
              onClick={() => selectTone(t)}
              disabled={loadingTone !== null}
              aria-pressed={isActive}
              title={t === 'Original' ? 'The original draft' : `Rewrite in a ${t.toLowerCase()} tone`}
            >
              {isLoading ? <span className={styles.toneSpinner} aria-label="Rewriting" /> : t}
            </button>
          );
        })}
      </div>
      {toneError && <div className={styles.toneError} role="alert">{toneError}</div>}

      {(block.to || hasSubject) && (
        <div className={styles.emailMeta}>
          {block.to && (
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>To</span>
              <span className={styles.emailMetaValue}>{block.to}</span>
            </div>
          )}
          {hasSubject && (
            <div className={styles.emailMetaRow}>
              <span className={styles.emailMetaLabel}>Subject</span>
              <input
                className={styles.emailSubjectInput}
                value={subject}
                onChange={e => setSubject(e.target.value)}
                onBlur={commit}
                aria-label="Email subject"
                spellCheck
              />
            </div>
          )}
        </div>
      )}

      <textarea
        ref={bodyRef}
        className={styles.emailBodyInput}
        value={body}
        onChange={e => setBody(e.target.value)}
        onBlur={commit}
        aria-label="Email body"
        spellCheck
      />
    </div>
  );
}

export default function ChatBlocks({ blocks, onEditBlock, sellerProfile }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  return (
    <div className={styles.blocks}>
      {blocks.map((block, i) => {
        if (!block || typeof block !== 'object') return null;
        if (block.type === 'sections') return <SectionsBlock key={i} block={block} />;
        if (block.type === 'list')  return <ListBlock  key={i} block={block} />;
        if (block.type === 'table') return <TableBlock key={i} block={block} />;
        if (block.type === 'email') return <EmailBlock key={i} block={block} index={i} onEditBlock={onEditBlock} sellerProfile={sellerProfile} />;
        return null;
      })}
    </div>
  );
}
